import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from '../../components/chrome/Button.js';
import { HistoryClock } from '../../components/history/HistoryClock.js';
import { ToolHistorySheet } from '../../components/history/HistorySheet.js';
import { useToolHistory } from '../../hooks/useToolHistory.js';
import { ApiError } from '../../lib/api-error.js';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell.js';
import { PlaceAutocomplete, type SelectedPlace } from '../../components/onboarding/PlaceAutocomplete.js';
import { PaymentSheet } from '../../components/payment/PaymentSheet.js';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { ReportMarkdown } from '../../components/ReportMarkdown.js';
import { useAuth } from '../../store/auth.js';
import { useMe } from '../../hooks/useMe.js';
import { apiFetch } from '../../lib/api-client.js';
import { useSkuInvoice } from '../../hooks/useSkuInvoice.js';
import { HowItWorks } from '../../components/HowItWorks.js';
import styles from './AskQuestion.module.css';

function nowLocalInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AskQuestion(): JSX.Element {
  const { t } = useTranslation(['flows', 'common']);
  const jwt = useAuth((s) => s.jwt);
  const { data: me } = useMe();
  const { purchase, redeemCoupon } = useSkuInvoice();
  const history = useToolHistory('ask', false);
  const [showHistory, setShowHistory] = useState(false);

  const [question, setQuestion] = useState('');
  const [when, setWhen] = useState(nowLocalInput());
  const [place, setPlace] = useState<SelectedPlace | null>(null);
  const [short, setShort] = useState<string | null>(null);
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [full, setFull] = useState<string | null>(null);
  const [isLimitedPrev, setIsLimitedPrev] = useState(false);
  const [busy, setBusy] = useState<'idle' | 'asking' | 'unlocking'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);

  function clearResult(): void {
    setQuestion(''); setShort(null); setFull(null); setQuestionId(null);
    setIsLimitedPrev(false); setShowInvoice(false); setShowHistory(false); setError(null);
  }
  useEffect(() => { clearResult(); }, [history.resultId, history.userId]);
  useEffect(() => {
    if (!history.resultId || !history.detail.data) return;
    const saved = history.detail.data;
    setQuestion(saved.question); setQuestionId(saved.id); setShort(saved.shortAnswer);
    setFull(saved.paidAt && saved.fullAnswer ? saved.fullAnswer : null);
  }, [history.resultId, history.detail.data]);
  function startNew(): void { clearResult(); history.open(null); }

  useEffect(() => {
    if (!me) return;
    if (
      me.birthLat != null &&
      me.birthLng != null &&
      me.birthPlaceName != null &&
      me.birthTimezone != null
    ) {
      setPlace({
        placeId: '',
        placeName: me.birthPlaceName,
        state: null,
        country: null,
        countryCode: null,
        lat: me.birthLat,
        lng: me.birthLng,
        tz: me.birthTimezone,
      });
    }
  }, [me]);

  async function ask(): Promise<void> {
    if (busy !== 'idle') return;
    if (!jwt || question.trim().length < 3) { setError(t('flows:ask.tooShort')); return; }
    if (!place) { setError(t('flows:ask.noChart')); return; }
    setError(null); setBusy('asking'); setFull(null);
    const askedAt = new Date(when).toISOString();
    const res = await apiFetch('/api/v1/horary', jwt, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ question, askedAt, lat: place.lat, lng: place.lng, placeName: place.placeName }),
    });
    setBusy('idle');
    if (res.status === 402) {
      const body = (await res.json()) as { questionId: string | null; shortAnswer: string | null };
      if (body.questionId && body.shortAnswer) {
        setQuestionId(body.questionId);
        setShort(body.shortAnswer);
        setIsLimitedPrev(true);
      } else {
        setError(t('flows:ask.limit'));
      }
      return;
    }
    if (!res.ok) { setError(t('flows:ask.error')); return; }
    const data = (await res.json()) as { questionId: string; shortAnswer: string };
    setQuestionId(data.questionId);
    setShort(data.shortAnswer);
  }

  async function unlock(): Promise<void> {
    setShowInvoice(true);
  }

  async function doPurchase(): Promise<void> {
    if (!jwt || !questionId) return;
    try {
      const { status } = await purchase('horary_full', {});
      if (status !== 'paid') return;
      setShowInvoice(false);
      await deliverFull();
    } catch {
      setBusy('idle'); setError(t('flows:ask.error'));
    }
  }

  async function doRedeemCoupon(code: string): Promise<void> {
    if (!questionId) throw new Error('no_question');
    await redeemCoupon('horary_full', code, {});
    setShowInvoice(false);
    await deliverFull();
  }

  async function deliverFull(): Promise<void> {
    if (!jwt || !questionId) return;
    setBusy('unlocking');
    const res = await apiFetch(`/api/v1/horary/${questionId}/deliver`, jwt, { method: 'POST' });
    setBusy('idle');
    if (!res.ok) { setError(t('flows:ask.error')); return; }
    const data = (await res.json()) as { fullAnswer: string };
    setFull(data.fullAnswer);
  }

  return (
    <MeSubpageShell title={t('flows:ask.title')} right={<HistoryClock label={t('history.open')} testId="ask-history" disabled={busy !== 'idle'} onClick={() => setShowHistory(true)} />}>
      <div className={styles.body}>
        <HowItWorks toolKey="ask" />
        {history.resultId !== null && history.detail.isPending ? <p role="status">{t('common:states.loading')}</p> : history.resultId !== null && history.detail.isError ? <div>
          <p role="alert">{t(history.detail.error instanceof ApiError && history.detail.error.status === 404 ? 'history.notFound' : 'history.loadError')}</p>
          <SecondaryButton onClick={() => void history.detail.refetch()}>{t('history.retry')}</SecondaryButton>
          <SecondaryButton onClick={startNew}>{t('history.new')}</SecondaryButton>
        </div> : short == null ? (
          <>
            <label className={styles.label}>{t('flows:ask.questionLabel')}</label>
            <textarea
              className={styles.textarea} value={question} maxLength={300}
              onChange={(e) => { if (history.resultId) history.open(null, true); setQuestion(e.target.value); }}
              placeholder={t('flows:ask.questionPlaceholder')}
            />
            <label className={styles.label}>{t('flows:ask.whenLabel')}</label>
            <input
              className={styles.input} type="datetime-local" value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            <label className={styles.label}>{t('flows:ask.placeLabel')}</label>
            <PlaceAutocomplete
              value={place} onChange={setPlace}
              ariaLabel={t('flows:ask.placeLabel')}
            />
            {error && <p className={styles.error} role="alert">{error}</p>}
            <PrimaryButton onClick={() => void ask()}
              disabled={busy === 'asking' || question.trim().length < 3}
            >
              {busy === 'asking' ? t('flows:ask.working') : t('flows:ask.submit')}
            </PrimaryButton>
          </>
        ) : (
          <>
            {isLimitedPrev && (
              <p className={styles.notice}>{t('flows:ask.limit')}</p>
            )}
            <div className={styles.answer}><ReportMarkdown markdown={short} /></div>
            {full == null ? (
              <PrimaryButton onClick={() => void unlock()}
                disabled={busy === 'unlocking'}
              >
                {busy === 'unlocking' ? t('flows:ask.fullWorking') : t('flows:ask.full', { stars: SKU_CATALOG['horary_full'].stars })}
              </PrimaryButton>
            ) : (
              <div className={styles.answer}><ReportMarkdown markdown={full} /></div>
            )}
            {error && <p className={styles.error} role="alert">{error}</p>}
          </>
        )}
        {short != null && <SecondaryButton onClick={startNew} disabled={busy !== 'idle'} data-testid="ask-reset">{t('history.new')}</SecondaryButton>}
      </div>

      {showHistory && <ToolHistorySheet kind="ask" currentId={questionId} onClose={() => setShowHistory(false)} onOpen={id => { setShowHistory(false); history.open(id); }} onDeleted={id => { if (id === questionId || id === history.resultId) { startNew(); setShowHistory(false); } }} />}

      {showInvoice && (
        <PaymentSheet
          skuCode="horary_full"
          title={t('flows:ask.full', { stars: SKU_CATALOG['horary_full'].stars })}
          priceStars={SKU_CATALOG['horary_full'].stars}
          bullets={[]}
          isPaying={busy === 'unlocking'}
          onPay={() => void doPurchase()}
          onCancel={() => setShowInvoice(false)}
          onApplyCoupon={doRedeemCoupon}
        />
      )}
    </MeSubpageShell>
  );
}
